import React, { useEffect, useRef, useState } from 'react';
import './MoodGames.css';

/**
 * HappyBurst — tap the floating smileys before they drift away. Each tap pops one
 * with a +1; reach the goal and the game celebrates. Open-ended and easy to leave;
 * nothing is scored against the person. New faces appear on a gentle timer.
 */
const EMOJIS = ['😀', '😄', '😁', '🥳', '😊', '🤩', '😸', '🌟'];
const GOAL = 7;

interface Face { id: number; left: number; top: number; emoji: string; pop?: boolean; }

export const HappyBurst: React.FC = () => {
  const seq = useRef(0);
  const [faces, setFaces] = useState<Face[]>([]);
  const [score, setScore] = useState(0);
  const done = score >= GOAL;

  useEffect(() => {
    if (done) return;
    const spawn = () => {
      setFaces((p) => {
        if (p.length >= 4) return p;
        const id = seq.current++;
        const face: Face = {
          id,
          left: 10 + Math.random() * 80,
          top: 14 + Math.random() * 72,
          emoji: EMOJIS[Math.floor(Math.random() * EMOJIS.length)],
        };
        // Auto-remove if not tapped in time.
        window.setTimeout(() => setFaces((q) => q.filter((f) => f.id !== id)), 2000);
        return [...p, face];
      });
    };
    spawn();
    const t = window.setInterval(spawn, 700);
    return () => window.clearInterval(t);
  }, [done]);

  const pop = (face: Face) => {
    if (face.pop) return;
    setFaces((p) => p.map((f) => (f.id === face.id ? { ...f, pop: true } : f)));
    setScore((s) => s + 1);
    window.setTimeout(() => setFaces((p) => p.filter((f) => f.id !== face.id)), 300);
  };

  const replay = () => { setScore(0); setFaces([]); };

  return (
    <div className="mm-game mm-game--burst">
      <div className="mm-game__head">
        <h3 className="mm-game__title">Happy Burst</h3>
        <p className="mm-game__sub">Tap the smileys before they float away.</p>
      </div>

      {done ? (
        <div className="mm-game__done">
          <span className="mm-game__done-emoji">🎉</span>
          <span className="mm-game__done-title">You did it!</span>
          <span className="mm-game__done-sub">Nice bursts of joy. Celebrate the little things.</span>
          <span className="mm-game__point">✨ +1 Joy Point</span>
          <button type="button" className="mm-game__btn mm-game__btn--ghost" onClick={replay}>Play again</button>
        </div>
      ) : (
        <>
          <div className="mm-game__meta">{score} / {GOAL}</div>
          <div className="mm-game__field" aria-label="Smiley field">
            {faces.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`mm-burst__face ${f.pop ? 'is-pop' : ''}`}
                aria-label="Pop smiley"
                style={{ left: `${f.left}%`, top: `${f.top}%` }}
                onClick={() => pop(f)}
              >
                {f.emoji}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HappyBurst;
